import { useQueryClient } from '@tanstack/react-query';
import { type FormEvent, useState } from 'react';
import { useNavigate } from 'react-router';

import { errorMessage } from '@/shared/api/errors';
import { Button } from '@/shared/ui/Button';
import { Callout } from '@/shared/ui/Callout';
import { Card } from '@/shared/ui/Card';
import { PasswordInput } from '@/shared/ui/PasswordInput';
import { PageHeader } from '@/shared/ui/PageHeader';

import { sessionQueryKey } from './api';
import { featuresOf } from '@/shared/session/currentUser';
import { MIN_PASSWORD, PASSWORD_HINT } from '@/shared/session/password';
import { PROFILE_HINTS, PROFILE_LABELS } from '@/shared/session/profiles';
import { SignaturesCard } from '@/features/signatures/SignaturesCard';

import { TwoFactorCard } from './TwoFactorCard';
import { useChangePassword, useChangeSignature, useSession } from './useSession';

/**
 * One's own account: change the password. It is also where a person lands when an
 * administrator typed their password for them (`must_change_password`): the API refuses
 * everything else until they choose their own, and this page says so.
 *
 * One card, the fields stacked: there is one thing to do here.
 *
 * «Tu perfil» (contract v10.47) says what the person sees of the prices, so a shop clerk who
 * misses the purchase price knows it is the profile and who to ask, not a fault.
 *
 * Under it, «Doble factor» (contract v10.45), only when the back speaks of it
 * (`features.two_factor`; an older back does not) and not while the password has to be
 * changed first, when the API refuses that card too.
 */
export function ProfilePage() {
  const session = useSession();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const change = useChangePassword();
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [repeat, setRepeat] = useState('');
  const mustChange = session.data?.must_change_password === true;
  const mismatch = repeat !== '' && next !== repeat;

  const platform = session.data?.is_superadmin === true;
  // Contract v10.47: what the person sees of the prices. A superadministrator sees it all,
  // whatever the column says; a back without profiles sends none.
  const profile = platform ? undefined : session.data?.profile;
  const who = [
    session.data?.email,
    platform ? 'Superadministrador' : session.data?.is_admin ? 'Administrador' : null,
    profile ? PROFILE_LABELS[profile] : null,
  ]
    .filter(Boolean)
    .join(' · ');
  // Contract v10.12: the mailboxes this person reads, which is what the inbox shows.
  const mailboxes = session.data?.mailboxes ?? [];
  const twoFactor = featuresOf(session.data).two_factor;

  function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (mismatch) return;
    change.mutate(
      { current_password: current, new_password: next },
      {
        onSuccess: (user) => {
          queryClient.setQueryData(sessionQueryKey, user);
          setCurrent('');
          setNext('');
          setRepeat('');
          if (mustChange) void navigate('/correos', { replace: true });
        },
      },
    );
  }

  return (
    <>
      <PageHeader
        title={session.data?.full_name ?? session.data?.email ?? 'Mi cuenta'}
        description={who || undefined}
      />

      {session.data ? (
        <Card title="Tus buzones" description="Lo que ves en la bandeja es lo que entra en estos.">
          {platform ? (
            <p className="text-ink-2 text-[14px]">
              Todos: un superadministrador ve todos los buzones.
            </p>
          ) : mailboxes.length ? (
            <ul className="text-ink flex flex-wrap gap-2 text-[14px]">
              {mailboxes.map((each) => (
                <li key={each} className="bg-paper-2 rounded-md px-2 py-1 font-mono text-[13px]">
                  {each}
                </li>
              ))}
            </ul>
          ) : (
            <p className="text-ink-2 text-[14px]">
              Ninguno todavía. Pídele a quien administra la aplicación que te asigne el buzón que
              lees.
            </p>
          )}
        </Card>
      ) : null}

      {profile ? (
        <Card
          title="Tu perfil"
          description="Lo que ves de los precios, los clientes y los presupuestos."
        >
          <p className="text-ink text-[14px] font-medium">{PROFILE_LABELS[profile]}</p>
          <p className="text-ink-2 text-[14px]">{PROFILE_HINTS[profile]}.</p>
          <p className="text-ink-3 mt-1 text-[13px]">Lo cambia quien administra la aplicación.</p>
        </Card>
      ) : null}

      {/* With a password to change first the API refuses everything else, this too. */}
      {session.data && !mustChange ? <SignaturesCard /> : null}
      {session.data && !mustChange ? <SignatureCard saved={session.data.signature ?? ''} /> : null}

      <Card
        title="Cambiar la contraseña"
        description="Cierra las demás sesiones abiertas con tu cuenta; esta se queda."
        className="max-w-lg"
      >
        {mustChange ? (
          <Callout tone="warn" title="Elige tu contraseña antes de seguir" role="alert">
            La contraseña con la que has entrado la puso un administrador y la ha visto más gente
            que tú. Hasta que elijas la tuya, el resto de la aplicación no responde.
          </Callout>
        ) : null}

        <form onSubmit={submit} className="flex flex-col gap-3">
          <PasswordInput
            label="Contraseña actual"
            autoComplete="current-password"
            value={current}
            onChange={(event) => setCurrent(event.target.value)}
            required
          />
          <PasswordInput
            label="Contraseña nueva"
            autoComplete="new-password"
            value={next}
            onChange={(event) => setNext(event.target.value)}
            minLength={MIN_PASSWORD}
            hint={PASSWORD_HINT}
            required
          />
          <PasswordInput
            label="Repite la nueva"
            autoComplete="new-password"
            value={repeat}
            onChange={(event) => setRepeat(event.target.value)}
            error={mismatch ? 'No coinciden.' : undefined}
            required
          />
          <div className="flex items-center gap-3">
            <Button
              type="submit"
              disabled={change.isPending || next.length < MIN_PASSWORD || mismatch || !current}
            >
              {change.isPending ? 'Cambiando…' : 'Cambiar la contraseña'}
            </Button>
            <p role="status" className="text-ok text-[13.5px]">
              {change.isSuccess ? 'Cambiada.' : ''}
            </p>
          </div>
          {change.isError ? (
            <p role="alert" className="text-danger text-[13.5px]">
              {errorMessage(change.error)}
            </p>
          ) : null}
        </form>
      </Card>

      {session.data && !mustChange && twoFactor ? (
        <TwoFactorCard account={session.data.email} availability={twoFactor} />
      ) : null}
    </>
  );
}

/** As many characters as the back keeps (`MAX_SIGNATURE`). */
const MAX_SIGNATURE = 2000;

/**
 * The plain text signature of the mail manager (contract v10.41): one per person, whatever
 * the mailbox. Since v10.57 it is the one of the mailboxes where the person has none with
 * format (`SignaturesCard`), and the form shows it under the text, apart.
 */
function SignatureCard({ saved }: { saved: string }) {
  const change = useChangeSignature();
  const [text, setText] = useState(saved);
  const [base, setBase] = useState(saved);
  // The session can change under the card (another tab saved one): follow it unless the
  // person is writing.
  if (saved !== base) {
    setBase(saved);
    if (text === base) setText(saved);
  }
  const dirty = text.trim() !== saved.trim();

  function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    change.mutate(text);
  }

  return (
    <Card
      title="Firma de texto"
      description="Sin formato ni imágenes. Sale en los buzones donde no tienes firma con formato."
      className="max-w-lg"
    >
      <form onSubmit={submit} className="flex flex-col gap-3">
        <label className="text-ink-2 flex flex-col gap-1 text-[13.5px] font-medium">
          Tu firma
          <textarea
            className="field h-auto py-2 font-normal"
            rows={5}
            maxLength={MAX_SIGNATURE}
            value={text}
            placeholder={'Nombre\nFerretería Cubas\nTeléfono'}
            onChange={(event) => setText(event.target.value)}
          />
        </label>
        <div className="flex items-center gap-3">
          <Button type="submit" disabled={change.isPending || !dirty}>
            {change.isPending ? 'Guardando…' : text.trim() ? 'Guardar la firma' : 'Quitar la firma'}
          </Button>
          <p role="status" className="text-ok text-[13.5px]">
            {change.isSuccess && !dirty ? 'Guardada.' : ''}
          </p>
        </div>
        {change.isError ? (
          <p role="alert" className="text-danger text-[13.5px]">
            {errorMessage(change.error)}
          </p>
        ) : null}
      </form>
    </Card>
  );
}
